import {
  AfterViewInit,
  Component,
  forwardRef,
  Injector,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { NgbDate, NgbDatepicker, NgbDateStruct, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoService } from '@ngneat/transloco';
import { AbstractComponent } from '../abstract.component';
import { generateFormId } from '@utility';
import { DropdownComponent } from "../dropdown/dropdown.component";
import {DateTimeWithMilisecondsStruct} from "@model";
import {skip} from "rxjs";

@Component({
  selector: 'app-date-time-input',
  templateUrl: './date-time-input.component.html',
  styleUrls: ['./date-time-input.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => DateTimeInputComponent) },
    { provide: NG_VALIDATORS, multi: true, useExisting: forwardRef(() => DateTimeInputComponent) }
  ],
  standalone: false
})
export class DateTimeInputComponent extends AbstractComponent implements AfterViewInit, OnInit, OnChanges, ControlValueAccessor, Validators {
  static readonly PAGE_SIZE = 15;
  readonly currentDate = new Date();
  readonly CalendarState = CalendarState;

  @Input() openDate?: Date = new Date();
  @Input() displayMilliseconds = false;
  @Input() displayTime = false;
  @Input() displaySeconds = false;
  @Input() label = '';
  @Input() maxDate?: Date;
  @Input() minDate?: Date;
  @Input() hideErrorMessage = false;
  @Input() formControlName?: string;
  @Input() isFullWidth = true;
  @Input() hideLabel = false;
  @Input() allowOnlyYearSelection = false;
  @Input() ranges?: { from: Date | undefined, to: Date | undefined };
  @Input() invalidRangesMessageKey?: string;

  @ViewChild('calendar') calendarInstance?: NgbDatepicker;
  @ViewChild('dropdown', { static: false }) dropdown?: DropdownComponent;

  calendarState: CalendarState = CalendarState.DAY;
  months: NavigationObject[] = [];
  years: NavigationObject[] = [];
  navigationDate: NgbDate = this.getNgbDate(this.currentDate);

  inputForm = new FormGroup({
    inputDate: new FormControl<DateTimeWithMilisecondsStruct | null>(null),
    date: new FormControl<NgbDateStruct | null>(null),
    time: new FormControl<NgbTimeStruct | null>(null),
    milliseconds: new FormControl<number | null>(null),
    onlyYear: new FormControl<boolean>(false)
  });

  disabled = false;
  formId?: string;
  invalid = false;
  errors: ValidationErrors | null = null;
  isTouched = false;

  defaultMinDate = new Date(this.currentDate.getFullYear() - 100, this.currentDate.getMonth(), this.currentDate.getDate());
  defaultMaxDate = new Date(this.currentDate.getFullYear() + 10, this.currentDate.getMonth(), this.currentDate.getDate());

  private isUpdatingFromInput = false;
  private isUpdatingFromCalendar = false;

  private outerControl?: AbstractControl | null;

  onChange = (_: Date | null) => {};
  onTouched = () => {};

  constructor(private controlContainer: ControlContainer, private inj: Injector, public translocoService: TranslocoService) {
    super();
  }

  ngOnInit() {
    this.formId = generateFormId(this.formControlName);
    this.initMonths();
    this.setupValueChangeSubscriptions();
    this.configureTimeControls();
  }

  ngAfterViewInit() {
    this.outerControl = this.inj.get(NgControl).control;
    if (this.outerControl) {
      this.outerControl.markAsTouched = () => {
        this.isTouched = true;
      };
      this.outerControl.markAsUntouched = () => {
        this.isTouched = false;
      };
    }
    setTimeout(() => this.outerControl?.updateValueAndValidity());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['maxDate'] || changes['minDate'] || changes['ranges']) {
      this.outerControl?.updateValueAndValidity();
    }
  }

  private setupValueChangeSubscriptions(): void {
    this.subscriptions.push(
        this.inputForm.controls.inputDate.valueChanges.subscribe(dateTimeStruct => {
          if (!this.isUpdatingFromCalendar) {
            this.isUpdatingFromInput = true;
            this.updateFromInputDate(dateTimeStruct);
            this.isUpdatingFromInput = false;
          }
          this.emitChanges();
        }),

        this.inputForm.controls.date.valueChanges.subscribe(() => {
          if (!this.isUpdatingFromInput) {
            this.isUpdatingFromCalendar = true;
            this.updateFromCalendar();
            this.isUpdatingFromCalendar = false;
          }
          this.emitChanges();
        }),

        this.inputForm.controls.time.valueChanges.subscribe(() => {
          if (!this.isUpdatingFromInput) {
            this.isUpdatingFromCalendar = true;
            this.updateFromCalendar();
            this.isUpdatingFromCalendar = false;
          }
          this.emitChanges();
        })
    );

    if (this.allowOnlyYearSelection) {
      this.subscriptions.push(
          this.inputForm.controls.onlyYear.valueChanges.pipe(skip(1)).subscribe((val) => {
            this.inputForm.controls.inputDate.setValue(null);
            this.changeCalendarState(CalendarState.YEAR);
            if (val) {
              this.inputForm.controls.time.disable();
              this.inputForm.controls.milliseconds.disable();
            }
            else {
              this.configureTimeControls();
            }
          })
      );
    }
    if (this.displayMilliseconds) {
      this.subscriptions.push(
          this.inputForm.controls.milliseconds.valueChanges.subscribe(() => {
            if (!this.isUpdatingFromInput) {
              this.isUpdatingFromCalendar = true;
              this.updateFromCalendar();
              this.isUpdatingFromCalendar = false;
            }
            this.emitChanges();
          })
      );
    }
  }

  private configureTimeControls(): void {
    if (!this.displayTime) {
      this.inputForm.controls.time.disable();
      this.inputForm.controls.milliseconds.disable();
    } else {
      this.inputForm.controls.time.enable();
      this.displayMilliseconds
          ? this.inputForm.controls.milliseconds.enable()
          : this.inputForm.controls.milliseconds.disable();
    }
  }

  private updateFromInputDate(dateTimeStruct: DateTimeWithMilisecondsStruct| null): void {
    if (!dateTimeStruct?.date || !this.isValidDateStruct(dateTimeStruct.date)) {
      this.inputForm.patchValue(
          { date: null, time: { hour: 0, minute: 0, second: 0 }, milliseconds: 0 },
          { emitEvent: false }
      );
      return;
    }

    this.setFormValues(dateTimeStruct);
  }

  private setFormValues(dateTimeStruct: DateTimeWithMilisecondsStruct): void {
    if (!dateTimeStruct.date) return;

    const time: NgbTimeStruct = dateTimeStruct.time ?? { hour: 0, minute: 0, second: 0 };
    const milliseconds = dateTimeStruct.time?.millisecond ?? 0;

    this.inputForm.patchValue({
      date: dateTimeStruct.date,
      time,
      milliseconds
    }, { emitEvent: false });

    this.updateDateSelection(
        dateTimeStruct.date.year,
        dateTimeStruct.date.month - 1,
        dateTimeStruct.date.day
    );
  }

  private updateFromCalendar(): void {
    const date = this.inputForm.controls.date.value;

    if (!date) {
      this.inputForm.controls.inputDate.setValue(null, { emitEvent: false });
      return;
    }

    const time = this.inputForm.controls.time.value;
    const milliseconds = this.inputForm.controls.milliseconds.value;

    const dateTimeStruct: DateTimeWithMilisecondsStruct = {
      date,
      time: time ? { ...time, millisecond: milliseconds ?? undefined } : undefined
    };

    this.inputForm.controls.inputDate.setValue(dateTimeStruct, { emitEvent: false });
  }

  private convertToDate(dateTimeStruct: DateTimeWithMilisecondsStruct): Date | null {
    if (!dateTimeStruct.date) return null;
    const seconds = this.displaySeconds ? (dateTimeStruct.time?.second ?? 0) : 0
    const miliseconds = this.displayMilliseconds ? (dateTimeStruct.time?.millisecond ?? 0) : 0
    return new Date(
        dateTimeStruct.date.year,
        dateTimeStruct.date.month - 1,
        dateTimeStruct.date.day,
        dateTimeStruct.time?.hour ?? 0,
        dateTimeStruct.time?.minute ?? 0,
        seconds,
        miliseconds
    );
  }


  private convertFromDate(date: Date): DateTimeWithMilisecondsStruct {
    return {
      date: {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate()
      },
      time: this.displayTime ? {
        hour: date.getHours(),
        minute: date.getMinutes(),
        second: this.displaySeconds ? date.getSeconds() : 0,
        millisecond: this.displayMilliseconds ? date.getMilliseconds(): 0
      } : undefined
    };
  }

  private isValidDateStruct(date: NgbDateStruct): boolean {
    if (!date?.year || !date.month || !date.day) return false;
    const testDate = new Date(date.year, date.month - 1, date.day);
    return !isNaN(testDate.getTime());
  }


  changeCalendarState(calendarState: CalendarState): void {
    if (this.calendarState !== calendarState) {
      this.setCalendarState(calendarState);
      if (this.calendarState === CalendarState.YEAR) {
        this.initYears();
      }
      else if (this.calendarState === CalendarState.MONTH) {
        this.updateMonths();
      }
    }
  }

  initMonths(): void {
    this.months = Array.from({ length: 12 }, (_, i) => new NavigationObject(i + 1, false));
  }

  updateMonths(): void {
    const { year: maxYear, month: maxMonth } = this.maxCalendarDate;
    const { year: minYear, month: minMonth } = this.minCalendarDate;
    const currentYear = this.navigationDate.year;

    this.months.forEach(month => {
      month.disabled = (
          (currentYear === maxYear && month.value > maxMonth) ||
          (currentYear === minYear && month.value < minMonth) ||
          currentYear > maxYear || currentYear < minYear
      );
    });
  }

  initYears(): void {
    const centerYear = this.navigationDate.year;
    const from = centerYear - 7;
    const to = centerYear + 7;
    const { year: minYear } = this.minCalendarDate;
    const { year: maxYear } = this.maxCalendarDate;

    this.years = Array.from(
        { length: to - from + 1 },
        (_, i) => {
          const year = from + i;
          return new NavigationObject(year, year < minYear || year > maxYear);
        }
    );
  }


  yearSelected(event: Event | undefined, year: number): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.navigationDate.year = year;

    if (this.inputForm.controls.onlyYear.value) {
      this.calendarState = CalendarState.YEAR;
      this.updateDateSelection(year, 0, 1);
      return;
    }

    const currentValue = this.inputForm.controls.date.value;
    if (!currentValue) {
      this.navigateCalendar(this.navigationDate);
      return;
    }

    const maxDayInMonth = new Date(year, currentValue.month, 0).getDate();
    const validDay = Math.min(currentValue.day, maxDayInMonth);

    this.updateDateSelection(year, currentValue.month - 1, validDay);
  }

  monthSelected(event: Event, month: number): void {
    event.preventDefault();
    event.stopPropagation();
    this.navigationDate.month = month;
    this.setCalendarState(CalendarState.DAY);

    const currentValue = this.inputForm.controls.date.value;
    if (!currentValue) {
      this.navigateCalendar(this.navigationDate);
      return;
    }

    const maxDayInMonth = new Date(currentValue.year, month, 0).getDate();
    const validDay = Math.min(currentValue.day, maxDayInMonth);

    this.updateDateSelection(currentValue.year, month - 1, validDay);
  }

  private updateDateSelection(year: number, month: number, day: number): void {
    const selectedDate = new Date(year, month, day);
    this.navigationDate = new NgbDate(
        selectedDate.getFullYear(),
        selectedDate.getMonth() + 1,
        selectedDate.getDate()
    );

    this.inputForm.controls.date.setValue({
      year: selectedDate.getFullYear(),
      month: selectedDate.getMonth() + 1,
      day: selectedDate.getDate()
    });

    this.navigateCalendar(this.navigationDate);
  }


  navigateNext(): void {
    this.calendarState === CalendarState.DAY ? this.navigateToNextMonth() : this.navigateNextYears();
  }

  navigatePrevious(): void {
    this.calendarState === CalendarState.DAY ? this.navigateToPreviousMonth() : this.navigatePreviousYears();
  }

  navigateNextYears(): void {
    const lastYear = this.years[this.years.length - 1];
    if (!lastYear || lastYear.value >= this.maxCalendarDate.year) return;

    const from = lastYear.value + 1;
    const { year: maxYear } = this.maxCalendarDate;

    this.years = Array.from(
        { length: DateTimeInputComponent.PAGE_SIZE },
        (_, i) => {
          const year = from + i;
          return new NavigationObject(year, year > maxYear);
        }
    );
  }

  navigatePreviousYears(): void {
    const firstYear = this.years[0];
    if (!firstYear || firstYear.value <= this.minCalendarDate.year) return;

    const to = firstYear.value - 1;
    const from = to - DateTimeInputComponent.PAGE_SIZE + 1;
    const { year: minYear } = this.minCalendarDate;

    this.years = Array.from(
        { length: DateTimeInputComponent.PAGE_SIZE },
        (_, i) => {
          const year = from + i;
          return new NavigationObject(year, year < minYear);
        }
    );
  }

  navigateToPreviousMonth(): void {
    const calendar = this.calendarInstance?.calendar;
    if (!calendar) return;

    const previous = calendar.getPrev(this.navigationDate, 'm');
    if (!previous.before(this.minCalendarDate)) {
      this.navigationDate = previous;
      this.calendarInstance?.navigateTo(this.navigationDate);
    }
  }

  navigateToNextMonth(): void {
    const calendar = this.calendarInstance?.calendar;
    if (!calendar) return;
    const next = calendar.getNext(this.navigationDate, 'm');
    if (!next.after(this.maxCalendarDate)) {
      this.navigationDate = next;
      this.calendarInstance?.navigateTo(this.navigationDate);
    }
  }

  navigateCalendar(navigation: NgbDateStruct): void {
    this.setCalendarState(CalendarState.DAY);
    this.calendarInstance?.navigateTo(navigation);
  }

  setCalendarState(calendarState: CalendarState) {
    this.calendarState = (this.inputForm.controls.onlyYear.value ?? false) ? CalendarState.YEAR : calendarState;
  }


  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    isDisabled ? this.inputForm.disable() : this.inputForm.enable();
  }

  writeValue(value: Date | null): void {
    if (!value) {
      this.inputForm.patchValue({
        inputDate: null,
        date: null,
        time: { hour: 0, minute: 0, second: 0 },
        milliseconds: 0,
        onlyYear: false
      }, { emitEvent: false });
      return;
    }

    const dateTimeStruct = this.convertFromDate(value);

    this.inputForm.patchValue({
      inputDate: dateTimeStruct,
      date: dateTimeStruct.date,
      time: dateTimeStruct.time ?? { hour: 0, minute: 0, second: 0 },
      milliseconds: dateTimeStruct.time?.millisecond ?? 0,
      onlyYear: false
    }, { emitEvent: false });
  }


  validate(control: AbstractControl): ValidationErrors | null {
    const errors: ValidationErrors = {};
    const dateTimeStruct = this.inputForm.controls.inputDate.value;
    if (!dateTimeStruct?.date) {
      const requiredError = control.hasValidator(Validators.required);
      if (requiredError) {
        this.invalid = true;
        this.errors = { required: true };
        return { required: true };
      }
      this.invalid = false;
      this.errors = null;
      return null;
    }

    const dateValue = this.convertToDate(dateTimeStruct);
    if (!dateValue || isNaN(dateValue.getTime())) {
      errors['invalidDate'] = { actual: dateTimeStruct };
      this.invalid = true;
      this.errors = errors;
      return errors;
    }

    if (dateValue.getTime() < this.minimumDateValue.getTime()) {
      const errorType : string = this.minimumDateValue.getTime() === this.defaultMinDate.getTime() ? 'defaultMinDate' : 'minDate';
      errors[errorType] = { min: this.minimumDateValue, actual: dateValue };
    }

    if (dateValue.getTime() > this.maximumDateValue.getTime()) {
      const errorType : string = this.maximumDateValue.getTime() === this.defaultMaxDate.getTime() ? 'defaultMaxDate' : 'maxDate';
      errors[errorType] = { max: this.maximumDateValue, actual: dateValue };
    }

    if (this.ranges) {
      const rangeFromDate = this.ranges.from ? new Date(this.ranges.from.getTime()) : null;
      const rangeToDate = this.ranges.to ? new Date(this.ranges.to.getTime()) : null;

      if (!this.displayTime) {
        rangeFromDate?.setHours(0);
        rangeToDate?.setHours(0);
        rangeFromDate?.setMinutes(0);
        rangeToDate?.setMinutes(0);
      }
      if (!this.displaySeconds) {
        rangeFromDate?.setSeconds(0);
        rangeToDate?.setSeconds(0);
      }
      if (!this.displayMilliseconds) {
        rangeFromDate?.setMilliseconds(0);
        rangeToDate?.setMilliseconds(0);
      }
      const rangeFrom = rangeFromDate?.getTime();
      const rangeTo = rangeToDate?.getTime();
      const currentTime = dateValue.getTime();

      if ((rangeFrom && currentTime < rangeFrom) || (rangeTo && currentTime > rangeTo)) {
        errors['dateOutOfRange'] = { ranges: this.ranges, actual: dateValue, message: this.invalidRangesMessageKey };
        this.isTouched = true;
        this.onTouched();
      }
    }
    this.invalid = Object.keys(errors).length > 0;
    this.errors = this.invalid ? errors : null;
    return this.invalid ? errors : null;
  }

  registerOnValidatorChange(fn: () => void): void {}


  get control(): FormControl {
    return (this.controlContainer?.control?.get(this.formControlName ?? '') as FormControl) ?? new FormControl();
  }

  get maxCalendarDate(): NgbDate {
    return this.getNgbDate(this.maximumDateValue);
  }

  get minCalendarDate(): NgbDate {
    return this.getNgbDate(this.minimumDateValue);
  }

  get minimumDateValue(): Date {
    return this.minDate ?? this.defaultMinDate;
  }

  get maximumDateValue(): Date {
    return this.maxDate ?? this.defaultMaxDate;
  }

  getNgbDate(date?: Date): NgbDate {
    const d = date ? new Date(date) : this.currentDate;
    d.setHours(0, 0, 0, 0);
    return new NgbDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
  }

  hasErrors(): boolean {
    return !!(this.inputForm.errors || this.control?.errors || this.errors);
  }

  closeDropdown(): void {
    this.dropdown?.close(false)
    this.markTouched();
  }

  markTouched(): void {
    this.inputForm.markAsTouched();
    this.isTouched = true;
    this.onTouched();
  }

  onDateSelected(_: NgbDateStruct): void {
    this.closeDropdown();
  }

  openCalendar(): void {
    this.navigationDate = this.getCalendarStartDate();
    this.setCalendarState(CalendarState.DAY);
  }

  getCalendarStartDate(): NgbDate {
    let navigationDate: Date = this.openDate ?? this.currentDate;
    const dateValue = this.inputForm.controls.date.value;
    let selectedDate: Date | undefined;

    if (dateValue) {
      selectedDate = new Date(dateValue.year, dateValue.month - 1, dateValue.day);
    }

    if (selectedDate && !this.hasErrors()) {
      navigationDate = selectedDate;
    } else if (this.ranges?.from) {
      navigationDate = this.ranges.from;
    } else if (this.ranges?.to) {
      navigationDate = this.ranges.to;
    } else if (this.minimumDateValue && navigationDate.getTime() < this.minimumDateValue.getTime()) {
      navigationDate = this.minimumDateValue;
    } else if (this.maximumDateValue && navigationDate.getTime() > this.maximumDateValue.getTime()) {
      navigationDate = this.maximumDateValue;
    }

    if (navigationDate.getTime() < this.minimumDateValue.getTime()) {
      navigationDate = this.minimumDateValue;
    } else if (navigationDate.getTime() > this.maximumDateValue.getTime()) {
      navigationDate = this.maximumDateValue;
    }

    return this.getNgbDate(navigationDate);
  }

  onIncrementMilliseconds(incrementNumber: number): void {
    const dateTimeStruct = this.inputForm.controls.inputDate.value;
    if (!dateTimeStruct?.date) return;

    const currentDate = this.convertToDate(dateTimeStruct);
    if (!currentDate) return;

    currentDate.setMilliseconds(currentDate.getMilliseconds() + incrementNumber);
    this.writeValue(currentDate);
    this.emitChanges();
  }

  emitChanges(): void {
    const dateTimeStruct = this.inputForm.controls.inputDate.value;
    const errors = this.validate(this.control);

    if (!dateTimeStruct || errors) {
      this.onChange(null);
      return;
    }

    const dateValue = this.convertToDate(dateTimeStruct);
    this.onChange(dateValue);
  }

}

enum CalendarState {
  DAY,
  MONTH,
  YEAR
}

class NavigationObject {
  constructor(
      public value: number,
      public disabled: boolean
  ) {}
}
